import {
  getAddressColumn,
  getBankAccountColumn,
  getBirthDateColumn,
  getCivilStatusColumn,
  getDeceaseDateColumn,
  getEmailColumn,
  getExternalIdColumn,
  getFiscalResidenceColumn,
  getGenderColumn,
  getInvestorTypeColumn,
  getLegalPersonColumn,
  getNameColumn,
  getNationalityColumn,
  getNroEmployeeColumn,
  getNroIdColumn,
  getPhoneColumn,
  getPhoneSecondaryColumn,
  getTypeIdColumn,
} from 'constants/getExcelColumns';

import { generateEmail } from 'utils/filters';
import checkIsValidEmail from 'utils/checkIsValidEmail';
import { isGeneratedEmail as checkIsGeneratedEmail } from 'utils/isGeneratedEmail';

import getIsNaturalPerson from './getIsNaturalPerson';
import getPartnerGenderFromExcel, {
  PartnerGender,
} from './getPartnerGenderFromExcel';
import { formatExcelDateForBulkUpload } from './formatExcelDateValue';
import { getPartnerCategoriesFromExcelRow } from './getPartnerCategoriesFromExcelRow';
import { getPartnerNotesFromExcelRow } from './getPartnerNotesFromExcelRow';
import { normalizeExcelCellValue } from './normalizeExcelHeader';
import { PartnerExcelDynamicColumns } from './parsePartnerExcelDynamicColumns';
import { validatePartnerBulkUploadRow } from './validatePartnerBulkUploadRow';

export type PartnerBulkUploadPayload = {
  name: string;
  cif: string;
  email: string;
  phone?: string;
  phoneSecondary?: string;
  externalId?: string;
  idType?: string;
  nationality?: string;
  gender?: PartnerGender;
  isNaturalPerson: boolean;
  birthdate?: string;
  deceaseDate?: string;
  civilStatus?: string;
  employeeNumber?: string;
  bankAccountNumber?: string;
  address?: {
    line1?: string;
    zip?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  taxAddress?: {
    line1?: string;
    zip?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  category?: string | null;
  categories?: string[];
  notes?: string[];
  source: 'UPLOAD';
  categoryLabel?: string;
  categoryAssignments?: Record<string, boolean>;
  isGeneratedEmail?: boolean;
  validationErrors?: string[];
  hasValidationErrors?: boolean;
};

type FormatContext = {
  actualSociety: any;
  partnerCategories: any[];
  uploadedCategoriesMap?: Record<string, string>;
  partnerIndex: number;
  dynamicExcelColumns?: PartnerExcelDynamicColumns;
  t?: (translationKey: string) => string;
};

const getPartnerCategoryId = (
  excelRow: Record<string, any>,
  partnerCategories: any[],
  uploadedCategoriesMap: Record<string, string> = {}
) => {
  const categoryName = getInvestorTypeColumn(excelRow);

  if (!categoryName) {
    return { categoryId: null, categoryLabel: '' };
  }

  const existingCategory = partnerCategories?.find(
    (partnerCategory) =>
      partnerCategory?.name?.toUpperCase() === categoryName?.toUpperCase()
  );

  if (existingCategory) {
    return {
      categoryId: existingCategory._id,
      categoryLabel: existingCategory.name,
    };
  }

  const uploadedId = uploadedCategoriesMap[categoryName];

  return {
    categoryId: uploadedId || null,
    categoryLabel: categoryName,
  };
};

export const formatPartnerForBulkUpload = (
  excelRow: Record<string, any>,
  context: FormatContext
): PartnerBulkUploadPayload => {
  const {
    actualSociety,
    partnerCategories,
    uploadedCategoriesMap,
    partnerIndex,
    dynamicExcelColumns,
    t,
  } = context;

  const rawEmail = getEmailColumn(excelRow);
  const email =
    rawEmail ||
    generateEmail(actualSociety, (actualSociety?.partnersCount?.total || 0) + partnerIndex + 1);
  const isEmailAutogenerated =
    !rawEmail || checkIsGeneratedEmail(email);

  const { categoryId, categoryLabel } = getPartnerCategoryId(
    excelRow,
    partnerCategories,
    uploadedCategoriesMap
  );

  const excelCategoryIds = getPartnerCategoriesFromExcelRow(
    excelRow,
    dynamicExcelColumns?.categoryColumns
  );

  const categoryAssignments: Record<string, boolean> = {};

  dynamicExcelColumns?.categoryColumns?.forEach(({ columnKey, categoryId: id }) => {
    categoryAssignments[id] = normalizeExcelCellValue(excelRow[columnKey]) === 'SI';
  });

  const categoriesSet = new Set<string>(excelCategoryIds);

  if (categoryId) {
    categoriesSet.add(categoryId);
  }

  const categories = [...categoriesSet];
  const notes = getPartnerNotesFromExcelRow(
    excelRow,
    dynamicExcelColumns?.noteColumnKeys
  );

  const payload: PartnerBulkUploadPayload = {
    name: getNameColumn(excelRow),
    cif: getNroIdColumn(excelRow),
    email,
    phone: getPhoneColumn(excelRow) || undefined,
    phoneSecondary: getPhoneSecondaryColumn(excelRow) || undefined,
    externalId: getExternalIdColumn(excelRow) || undefined,
    idType: getTypeIdColumn(excelRow) || undefined,
    nationality: getNationalityColumn(excelRow) || undefined,
    gender: getPartnerGenderFromExcel(getGenderColumn(excelRow)),
    isNaturalPerson: getIsNaturalPerson(getLegalPersonColumn(excelRow)),
    birthdate: formatExcelDateForBulkUpload(getBirthDateColumn(excelRow)),
    deceaseDate: formatExcelDateForBulkUpload(getDeceaseDateColumn(excelRow)),
    civilStatus: getCivilStatusColumn(excelRow) || undefined,
    employeeNumber: getNroEmployeeColumn(excelRow) || undefined,
    bankAccountNumber: getBankAccountColumn(excelRow) || undefined,
    address: getAddressColumn(excelRow),
    taxAddress: getFiscalResidenceColumn(excelRow),
    category: categoryId,
    categories: categories.length ? categories : undefined,
    notes: notes.length ? notes : undefined,
    source: 'UPLOAD',
    categoryLabel,
    categoryAssignments:
      Object.keys(categoryAssignments).length > 0
        ? categoryAssignments
        : undefined,
    isGeneratedEmail:
      isEmailAutogenerated || (!!rawEmail && !checkIsValidEmail(rawEmail)),
  };

  if (t) {
    const validation = validatePartnerBulkUploadRow(payload, { t });

    payload.validationErrors = validation.errors;
    payload.hasValidationErrors = !validation.isValid;
  }

  return payload;
};

type FormatPartnersContext = FormatContext & {
  selectedPartners: (Record<string, any> & { isExisting?: boolean })[];
  existingCifs: string[];
};

export const formatPartnersForBulkUpload = ({
  selectedPartners,
  existingCifs,
  actualSociety,
  partnerCategories,
  uploadedCategoriesMap = {},
  dynamicExcelColumns,
  t,
}: Omit<FormatPartnersContext, 'partnerIndex'>) => {
  const uniqueCifs = [
    ...new Set(selectedPartners.map((partner) => getNroIdColumn(partner))),
  ];

  const uniquePartners = uniqueCifs.map((cif) =>
    selectedPartners.find((partner) => getNroIdColumn(partner) === cif)
  );

  const partnersToUpload = uniquePartners.filter(
    (partner): partner is Record<string, any> & { isExisting?: boolean } => {
      if (!partner) return false;

      return !existingCifs.includes(getNroIdColumn(partner));
    }
  );

  return partnersToUpload.map((partner, index) =>
    formatPartnerForBulkUpload(partner, {
      actualSociety,
      partnerCategories,
      uploadedCategoriesMap,
      partnerIndex: index,
      dynamicExcelColumns,
      t,
    })
  );
};

export const getExistingCifsFromPartners = (
  currentPartners: { cif?: string }[] = []
) =>
  currentPartners
    ?.map((partner) => partner?.cif || null)
    ?.filter((cif): cif is string => Boolean(cif));
